import type { SVGProps } from 'react';
import type { PinGlyph, PinGlyphElement } from '@/shared/icons/pinGlyphs';

const STROKE_WIDTH = 2;

interface PinGlyphSvgProps extends SVGProps<SVGSVGElement> {
  glyph: PinGlyph;
  /** Used when `className` does not set width/height (e.g. `h-4 w-4`). */
  size?: number;
}

function renderElement(element: PinGlyphElement, style: PinGlyph['style'], key: string) {
  if (element.kind === 'line') {
    if (style !== 'stroke') {
      return null;
    }
    return (
      <line
        key={key}
        x1={element.x1}
        y1={element.y1}
        x2={element.x2}
        y2={element.y2}
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  }

  if (style === 'fill') {
    return (
      <path key={key} d={element.d} fill="currentColor" fillRule={element.fillRule} stroke="none" />
    );
  }

  return (
    <path
      key={key}
      d={element.d}
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  );
}

export default function PinGlyphSvg({ glyph, size = 24, className, ...props }: PinGlyphSvgProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={glyph.viewBox}
      width={className ? undefined : size}
      height={className ? undefined : size}
      aria-hidden
      className={className}
      fill="none"
      {...props}
    >
      {glyph.elements.map((element, index) =>
        renderElement(element, glyph.style, `${element.kind}-${index}`),
      )}
    </svg>
  );
}
